<script lang="ts">
  import Icon from './icon.svelte';

  /**
   * The click-to-expand FAQ stack.
   *
   * Sixteen questions across the two product pages — ten on Intelligence, six
   * on Cleaning — were ~50 lines of markup each, every one repeating the
   * question mark icon, the rotating chevron and the conditional answer, with
   * the index hard-coded into three separate places per question
   * (`toggleFAQ(7)`, `onkeypress ... toggleFAQ(7)`, `expandedFAQs[7]`). Adding
   * a question in the middle meant renumbering every one after it.
   *
   * This is now the site's only FAQ. There used to be a second,
   * `components/faq-accordion.svelte`, which the seven keyword pages used: two
   * columns of cards with grey icon tiles and Expand All / Collapse All
   * controls. One widget with two appearances depending on which page you
   * landed on, so it was deleted and those pages moved here — which also drops
   * the two control labels from their rendered text, the only content this
   * change removes.
   *
   * `expanded` is a `$state` object. On the Intelligence page the equivalent
   * was a plain `let`, which left that page's accordion depending on legacy
   * mode to be reactive at all; stating it as a rune removes the question.
   *
   * The answer is hidden with a class rather than withheld with `{#if}`, and
   * that is not cosmetic. Every page here is prerendered, so anything behind
   * `{#if}` is absent from the file a crawler receives: the answers existed
   * only after a click in a browser that ran the JavaScript. Sixteen pages of
   * written answers were therefore invisible to search engines and to the
   * language models that read raw HTML — and the `FAQPage` markup was
   * asserting text that genuinely was not on the page, which is the condition
   * Google drops a rich result for. Hidden content is indexed; absent content
   * is not.
   *
   * Answers accept inline HTML — several contain `<strong>` runs and one a
   * list of fields — and are authored in this repository, never user input.
   *
   * The trigger is a `div` with `role="button"`, which is what the product
   * pages had; the accordion it replaced used a real `<button>` and so got a
   * focus ring for free. Moving seven more pages onto this design would have
   * left 42 keyboard-focusable triggers with no focus state, so the ring is
   * stated explicitly, in the one `focus` hue the rest of the site now uses.
   */
  export type Faq = { question: string; answer: string };

  let {
    faqs,
    heading = 'FAQ',
    lead = '',
    bg = 'bg-surface',
    /** Intelligence centres a bare "FAQ" with a wide gap; Cleaning runs a lead under it. */
    headingClass = 'mb-10',
    width = 'max-w-4xl',
    /** Cleaning lets the heading run the full container width; Intelligence constrains it. */
    headingWidth = 'max-w-4xl'
  }: {
    faqs: Faq[];
    heading?: string;
    lead?: string;
    bg?: string;
    headingClass?: string;
    width?: string;
    headingWidth?: string;
  } = $props();

  let expanded = $state<Record<number, boolean>>({});

  function toggle(index: number) {
    expanded[index] = !expanded[index];
  }
</script>

<section class="{bg} section-y">
  <div class="mx-auto max-w-7xl px-6 lg:px-8">
    <div class="mx-auto {headingWidth}">
      <h2 class="t-heading text-center {headingClass}">
        {heading}
      </h2>
      {#if lead}
        <p class="t-lead mb-10 text-center">{lead}</p>
      {/if}
    </div>

    <div class="mx-auto {width}">
      <div class="space-y-6">
        {#each faqs as faq, index (index)}
          <div
            class="w-full rounded-xl border border-line bg-surface p-6 hover:border-line-brand transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
            onclick={() => toggle(index)}
            onkeypress={(e) => e.key === 'Enter' && toggle(index)}
            role="button"
            tabindex="0"
            aria-expanded={!!expanded[index]}
            aria-controls="faq-answer-{index}"
          >
            <div class="flex items-center w-full">
              <Icon name="question" class="text-action-text flex-shrink-0" />
              <h3 class="t-title-sm mb-0 text-left flex-grow mx-4">
                {faq.question}
              </h3>
              <Icon
                name="chevron-down"
                class="text-ink-muted flex-shrink-0 transform transition-transform {expanded[index]
                  ? 'rotate-180'
                  : ''}"
              />
            </div>
            <p
              id="faq-answer-{index}"
              class="t-body-sm mt-4 pl-9 {expanded[index] ? 'block' : 'hidden'}"
            >
              {@html faq.answer}
            </p>
          </div>
        {/each}
      </div>
    </div>
  </div>
</section>
